import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { moduleOf, moduleByKey } from '../modules';
import { openAskPaul } from '../utils/askPaul';
import { MODULE_ICONS, SparkleIcon, AccountIcon, LockIcon } from './NavIcons';
import { KnowledgeIcon } from './BrandIcons';
import { formatCredits } from '../utils/formatCredits';
import '../styles/AppNav.css';
import { KindIcon } from '../bom/tree/icons';

const COLLAPSE_KEY = 'orc_nav_collapsed';
const SALES_EMAIL = 'sales@orcanos.com';

// The nav's sections (2026-10-27). Every entry is ALWAYS listed; one this account is not
// licensed for shows a lock and a "contact Orcanos" card instead of opening.
const SECTIONS = [
  { title: 'Design Control / ALM', keys: ['trace', 'dhf', 'risk'] },
  { title: 'QMS', keys: ['review', 'training'] },
  { title: 'BOM', keys: ['bom', 'sbom'] },
];

/**
 * A nav row for something the account does not have. Hover / focus shows a card telling
 * the person to contact Orcanos, with the sales address; a click opens a mail to it.
 * The card is `position: fixed` because the nav's `overflow` would clip anything absolute.
 */
function LockedItem({ label, icon, collapsed }) {
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const [pos, setPos] = useState(null);
  const mailto = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(`Orcanos ${label}`)}`;

  const open = () => {
    clearTimeout(closeTimer.current);
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ top: r.top + r.height / 2, left: r.right + 10 });
  };
  // A short delay so the pointer can cross the gap into the card and click the address.
  const close = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPos(null), 150);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <>
      <a
        ref={ref}
        href={mailto}
        className="app-nav-item app-nav-item--locked"
        aria-label={`${label} — not in your plan. Contact Orcanos: ${SALES_EMAIL}`}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
      >
        {icon}
        {!collapsed && <span className="app-nav-label">{label}</span>}
        {!collapsed && <LockIcon className="app-nav-lock" aria-hidden="true" />}
      </a>
      {pos && (
        <div
          className="app-nav-locked-tip"
          role="tooltip"
          style={{ top: pos.top, left: pos.left }}
          onMouseEnter={open}
          onMouseLeave={close}
        >
          <div className="app-nav-locked-tip-head">
            <LockIcon className="app-nav-locked-tip-icon" aria-hidden="true" />
            {label}
          </div>
          <div className="app-nav-locked-tip-body">Not in your plan yet. Contact Orcanos to add it.</div>
          <a className="app-nav-locked-tip-mail" href={mailto}>{SALES_EMAIL}</a>
        </div>
      )}
    </>
  );
}

function moduleIcon(m) {
  const Icon = MODULE_ICONS[m.key];
  return Icon
    ? <Icon className="app-nav-icon" aria-hidden="true" />
    : <span className="app-nav-icon" aria-hidden="true">{m.glyph}</span>;
}

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function OrcanosLogo({ className = 'orcanos-logo' }) {
  return (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true">
      <path
        d="M 33.94 15.56 A 38 38 0 1 1 14.29 37.00"
        fill="none"
        stroke="currentColor"
        strokeWidth="13"
        strokeLinecap="round"
      />
      <circle cx="21.98" cy="24.33" r="8" fill="#f5821f" />
    </svg>
  );
}

/**
 * The top bar's left side while the left nav is shown — Ask Paul's breadcrumb, `[account] / page`.
 * The product name lives in the nav's brand block, so the bar names where you are instead.
 */
export function ShellCrumb({ accountName }) {
  const location = useLocation();
  const mod = moduleByKey(moduleOf(location.pathname));
  const title = /^\/bom\/sw-/.test(location.pathname) ? 'Software BOM' : mod?.name;
  return (
    <div className="shell-crumb">
      {accountName && (
        <>
          <div className="shell-crumb-account" title="Current account">
            <AccountIcon width="13" height="13" />
            {accountName}
          </div>
          <span className="shell-crumb-sep">/</span>
        </>
      )}
      <div className="shell-crumb-title">{title || 'Orcanos Compliance Suite'}</div>
    </div>
  );
}

/**
 * The platform's left navigation — every module in its section (Design Control / ALM, QMS,
 * BOM), then Compliance AI (Ask Paul) and Plugins. Unlicensed entries stay listed
 * but locked, with a "contact Orcanos" card (2026-10-27).
 *
 * Laid out like Ask Paul's sidebar (brand block on top, full viewport height, line icons, the
 * soft gradient selected state) so moving between the two products does not change the frame.
 *
 *   * **The active module is read off the URL** (`moduleOf`), never held in state, so a deep
 *     link, a browser Back and an in-app navigation can never disagree about where you are.
 *   * **Ask Paul is not a module.** It is a separate app opened in a new tab by silent login
 *     (Critical Note #31), so it has no route, can never be the active item, and sits in its
 *     own group below the modules with an ↗ that says it leaves the page.
 *
 * A module may add sub-items under its own entry while it is active (`subItems[key]`). The
 * BOM viewer uses that for its two views, because they are URLs (`/bom/boms`, `/bom/parts`)
 * rather than a toggle inside the page.
 *
 * `knowledge` adds Product Knowledge as its own entry under "Library". It lives under
 * `/trace/knowledge` but is read by more than one module (the trace builder, Risk Studio), so
 * it is reachable from wherever you are rather than from the Traceability toolbar only.
 */
function AppNav({ modules = [], bomParts = ['hardware'], connectors = [], askPaul = false, knowledge = false, subItems = {}, counts = {} }) {
  const location = useLocation();
  const active = moduleOf(location.pathname);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [paulBusy, setPaulBusy] = useState(false);
  const [paulError, setPaulError] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // Storage blocked — the pane still works, it just won't remember.
    }
  }, [collapsed]);

  // The Software BOM shares the BOM module (its routes are `/bom/sw-*`, its licence is a BOM
  // part, Note #112) but is its own root entry in the nav. `bomParts` says which halves this
  // account has; a backend that predates the split reports hardware only.
  const onSbom = /^\/bom\/sw-/.test(location.pathname);
  const activeKey = active === 'bom' && onSbom ? 'sbom' : active;
  const bomModule = moduleByKey('bom');
  const entryOf = (key) => (key === 'sbom'
    ? { ...bomModule, key: 'sbom', name: 'Software BOM', home: '/bom/sw-releases' }
    : moduleByKey(key));
  const isLicensed = (key) => {
    if (key === 'bom') return modules.includes('bom') && bomParts.includes('hardware');
    if (key === 'sbom') return modules.includes('bom') && bomParts.includes('sbom');
    return modules.includes(key);
  };
  // Integrations → "Plugins": one entry per connector, each locked unless licensed.
  const integrations = moduleByKey('integrations');
  const plugins = integrations.navEntries.map((e) => ({
    ...integrations,
    navLabel: e.label,
    home: e.to,
    entryKey: `${integrations.key}-${e.connector}`,
    connector: e.connector,
    licensed: modules.includes('integrations') && connectors.includes(e.connector),
  }));
  const typeParam = new URLSearchParams(location.search).get('type');

  const goAskPaul = async () => {
    if (paulBusy) return;
    setPaulBusy(true);
    setPaulError('');
    await openAskPaul(setPaulError);
    setPaulBusy(false);
  };

  return (
    <nav className={`app-nav${collapsed ? ' collapsed' : ''}`} aria-label="Modules">
      <div className="app-nav-brand">
        {collapsed ? (
          <button
            type="button"
            className="app-nav-brand-logo app-nav-brand-btn"
            onClick={() => setCollapsed(false)}
            title="Expand sidebar"
            aria-label="Expand navigation"
          >
            <OrcanosLogo className="app-nav-logo" />
          </button>
        ) : (
          <>
            <div className="app-nav-brand-logo">
              <OrcanosLogo className="app-nav-logo" />
            </div>
            <div className="app-nav-brand-text">
              <div className="app-nav-brand-name">Orcanos</div>
              <div className="app-nav-brand-sub">Compliance Suite</div>
            </div>
            <button
              type="button"
              className="app-nav-collapse-btn"
              onClick={() => setCollapsed(true)}
              title="Collapse sidebar"
              aria-label="Collapse navigation"
            >
              «
            </button>
          </>
        )}
      </div>

      {/* The splitter handle — the collapsed rail's edge is where people reach for the pane,
          so the expand affordance lives there too. The logo button stays as it was. */}
      {collapsed && (
        <button
          type="button"
          className="app-nav-edge-btn"
          onClick={() => setCollapsed(false)}
          title="Expand sidebar"
          aria-label="Expand navigation"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 3.5 10.5 8 6 12.5" />
          </svg>
        </button>
      )}

      {SECTIONS.map((sec) => (
        <div key={sec.title} className="app-nav-group">
          {!collapsed && <div className="app-nav-heading">{sec.title}</div>}
          {sec.keys.map((key) => {
            const m = entryOf(key);
            if (!isLicensed(key)) {
              return <LockedItem key={key} label={m.name} icon={moduleIcon(m)} collapsed={collapsed} />;
            }
            const isActive = activeKey === m.key;
            const subs = isActive ? subItems[m.key] || [] : [];
            return (
              <div key={m.key} className="app-nav-entry">
                <NavLink
                  to={m.home}
                  className={`app-nav-item${isActive ? ' active' : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                  title={collapsed ? m.name : undefined}
                >
                  {moduleIcon(m)}
                  {!collapsed && <span className="app-nav-label">{m.name}</span>}
                  {/* A count of work waiting in this module (Doc Review: reviews you owe). On the
                      collapsed rail it shrinks to a dot so the signal survives the collapse. */}
                  {counts[m.key] > 0 && (
                    <span
                      className={`app-nav-count${collapsed ? ' app-nav-count--dot' : ''}`}
                      title={`${counts[m.key]} waiting for you`}
                      aria-label={`${counts[m.key]} waiting for you`}
                    >
                      {collapsed ? '' : counts[m.key]}
                    </span>
                  )}
                </NavLink>
                {!collapsed && subs.length > 0 && (
                  <div className="app-nav-subs">
                    {subs.map((s) => (
                      <NavLink
                        key={s.to}
                        to={s.to}
                        className={({ isActive: on }) => `app-nav-sub${on ? ' active' : ''}`}
                      >
                        {s.icon && <KindIcon kind={s.icon} size={20} title="" />}
                        <span className="app-nav-sub-label">{s.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {knowledge && (
        <div className="app-nav-group">
          {!collapsed && <div className="app-nav-heading">Library</div>}
          <NavLink
            to="/trace/knowledge"
            className={({ isActive: on }) => `app-nav-item${on ? ' active' : ''}`}
            title={collapsed ? 'Product Knowledge' : undefined}
          >
            <KnowledgeIcon size={16} className="app-nav-icon" />
            {!collapsed && <span className="app-nav-label">Product Knowledge</span>}
          </NavLink>
        </div>
      )}

      <div className="app-nav-group app-nav-apps">
        {!collapsed && <div className="app-nav-heading">Compliance AI</div>}
        {askPaul ? (
          <button
            type="button"
            className="app-nav-item app-nav-paul"
            onClick={goAskPaul}
            disabled={paulBusy}
            title={collapsed ? 'Ask Paul — opens in a new tab' : 'Open Ask Paul — you are signed in automatically'}
          >
            <SparkleIcon className="app-nav-icon app-nav-paul-mark" aria-hidden="true" />
            {!collapsed && (
              <>
                <span className="app-nav-label">{paulBusy ? 'Opening…' : 'Ask Paul'}</span>
                <span className="app-nav-ext" aria-hidden="true">↗</span>
              </>
            )}
          </button>
        ) : (
          <LockedItem
            label="Ask Paul"
            icon={<SparkleIcon className="app-nav-icon app-nav-paul-mark" aria-hidden="true" />}
            collapsed={collapsed}
          />
        )}
        {paulError && (
          <div className="app-nav-error" role="alert">
            {paulError}
            <button type="button" className="app-nav-error-x" onClick={() => setPaulError('')} aria-label="Dismiss">×</button>
          </div>
        )}
      </div>

      <div className="app-nav-group">
        {!collapsed && <div className="app-nav-heading">Plugins</div>}
        {plugins.map((m) => {
          const label = m.navLabel || m.name;
          if (!m.licensed) {
            return <LockedItem key={m.entryKey} label={label} icon={moduleIcon(m)} collapsed={collapsed} />;
          }
          // A connector entry is active only on its own filtered view.
          const isActive = active === m.key && typeParam === m.connector;
          return (
            <NavLink
              key={m.entryKey}
              to={m.home}
              className={() => `app-nav-item${isActive ? ' active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              title={collapsed ? label : undefined}
            >
              {moduleIcon(m)}
              {!collapsed && <span className="app-nav-label">{label}</span>}
            </NavLink>
          );
        })}
      </div>

      <NavCredits collapsed={collapsed} />
    </nav>
  );
}

/**
 * Credits consumed this month, pinned to the bottom of the nav on every page (was in the
 * Dashboard's footer only, 2026-10-27). Polled every minute; the endpoint does not slide the
 * session. Always shown — "—" until the first answer, or when it cannot be read.
 */
function NavCredits({ collapsed }) {
  const [credits, setCredits] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => fetch('/api/account/credits', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setCredits(d); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  const n = credits ? formatCredits(credits.mine_month) : '—';
  const tip = credits
    ? `You consumed ${formatCredits(credits.mine_month)} credits this month (${formatCredits(credits.mine_total)} in total). Your account: ${formatCredits(credits.account_month)} this month, ${formatCredits(credits.account_total)} in total.`
    : 'Credits used this month';
  return (
    <div className="app-nav-credits" title={tip}>
      <span className="app-nav-credits-num">{n}</span>
      {!collapsed && <span className="app-nav-credits-label">credits this month</span>}
      {!collapsed && <span className="app-nav-credits-free">(No charge)</span>}
    </div>
  );
}

export default AppNav;
